import React from 'react';
import { Stethoscope, Heart, ChefHat } from 'lucide-react';
import { useLanguage } from '../context/AppContext';

/**
 * Clinical Mode Indicator Badge
 * Shows when Clinical Mode is active for a user
 * 
 * Variants:
 * - 'compact': Small badge for headers/navbars
 * - 'banner': Full-width banner with more details
 * - 'inline': Inline badge for cards
 */
const ClinicalModeIndicator = ({ 
  clinicalMode, 
  variant = 'compact',
  className = ''
}) => {
  const { language } = useLanguage();

  if (!clinicalMode?.enabled) return null;

  const conditionLabels = {
    cancer_treatment: { fr: 'Cancer', en: 'Cancer' },
    dialysis: { fr: 'Dialyse', en: 'Dialysis' },
    crohn: { fr: 'Crohn', en: 'Crohn' },
    bariatric: { fr: 'Bariatrique', en: 'Bariatric' },
    diabetes: { fr: 'Diabète', en: 'Diabetes' },
    celiac: { fr: 'Cœliaque', en: 'Celiac' },
    custom: { fr: 'Protocole', en: 'Protocol' }
  };

  const conditionLabel = conditionLabels[clinicalMode.condition_type] || conditionLabels.custom;

  if (variant === 'compact') {
    return (
      <div 
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest ${className}`}
        data-testid="clinical-mode-indicator-compact"
        title={language === 'fr' ? 'Mode Clinique Actif' : 'Clinical Mode Active'}
        style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(236,72,153,0.22) 100%)',
          border: '1px solid rgba(236,72,153,0.55)',
          boxShadow: '0 0 14px rgba(236,72,153,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
          backdropFilter: 'blur(6px)',
          color: '#fbcfe8',
          textShadow: '0 0 6px rgba(236,72,153,0.65)',
        }}
      >
        <Stethoscope className="w-3.5 h-3.5" style={{ filter: 'drop-shadow(0 0 5px rgba(236,72,153,0.9))' }} />
        <span>{language === 'fr' ? 'Clinique' : 'Clinical'}</span>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div 
        className={`bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-lg p-3 ${className}`}
        data-testid="clinical-mode-indicator-banner"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-pink-500/20 rounded-lg flex-shrink-0">
            <Stethoscope className="w-5 h-5 text-pink-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-pink-400">
                {language === 'fr' ? 'Mode Clinique Actif' : 'Clinical Mode Active'}
              </span>
              <span className="text-xs px-2 py-0.5 bg-pink-500/20 rounded-full text-pink-300">
                {language === 'fr' ? conditionLabel.fr : conditionLabel.en}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
              <ChefHat className="w-3 h-3" />
              {language === 'fr' 
                ? 'YUMIX adapté à votre protocole médical'
                : 'YUMIX adapted to your medical protocol'}
            </p>
          </div>
          <Heart className="w-4 h-4 text-pink-400 flex-shrink-0 animate-pulse" />
        </div>
      </div>
    );
  }

  // inline variant
  return (
    <div 
      className={`flex items-center gap-1.5 text-pink-400 ${className}`}
      data-testid="clinical-mode-indicator-inline"
    >
      <Stethoscope className="w-4 h-4" />
      <span className="text-xs font-medium">
        {language === 'fr' ? 'Mode Clinique' : 'Clinical Mode'}
      </span>
    </div>
  );
};

export default ClinicalModeIndicator;
